import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Check, X, RefreshCw, Users, Upload, FileText, Download, Loader2 } from "lucide-react";
import CampaignSurveysSection from "@/pages/CampaignSurveysSection";
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown";

const ATTACHMENT_TOTAL_LIMIT_BYTES = 25 * 1024 * 1024;

function humanBytes(n) {
  if (!n) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function CampaignAttachmentsSection({ campaignId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/attachments`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load attachments");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const totalBytes = items.reduce((s, it) => s + (it.size || 0), 0);
  const remainingBytes = Math.max(0, ATTACHMENT_TOTAL_LIMIT_BYTES - totalBytes);

  const onPick = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    if (file.size + totalBytes > ATTACHMENT_TOTAL_LIMIT_BYTES) {
      toast.error(`Adding ${humanBytes(file.size)} would exceed the 25 MB campaign cap. ${humanBytes(remainingBytes)} remaining.`);
      evt.target.value = "";
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    if (label.trim()) form.append("label", label.trim());
    try {
      await api.post(`/campaigns/${campaignId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      setLabel("");
      evt.target.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
      evt.target.value = "";
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (aid) => {
    if (!window.confirm("Delete this attachment? Agents will no longer be able to download it.")) return;
    try {
      await api.delete(`/campaigns/${campaignId}/attachments/${aid}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onDownload = async (attachment) => {
    try {
      const resp = await api.get(`/campaigns/${campaignId}/attachments/${attachment.id}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: attachment.mime_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    }
  };

  return (
    <div data-testid="campaign-attachments-section">
      <div className="flex items-center justify-between mb-2">
        <label className="meta-label">Script (attachments)</label>
        <span className="text-[10px] font-mono text-muted-foreground">
          {humanBytes(totalBytes)} / 25 MB used ({humanBytes(remainingBytes)} free)
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">
        Upload the campaign script + supporting docs. Agents will see them under Campaign Parameters on the Add Lead form. Allowed: PDF, DOC(X), XLS(X), PPT(X), TXT. Total cap: 25 MB.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-secondary/40 rounded-sm border border-border">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Optional label (e.g. Objection Handling)"
          className="h-8 rounded-sm text-xs flex-1 min-w-[220px]"
          data-testid="campaign-attach-label-input"
        />
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain"
          onChange={onPick}
          data-testid="campaign-attach-file-input"
        />
        <Button
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || remainingBytes <= 0}
          className="rounded-sm h-8"
          data-testid="campaign-attach-upload-btn"
        >
          {uploading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
          {uploading ? "Uploading…" : "Upload document"}
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic border border-dashed border-border rounded-sm p-3 text-center">
          No documents yet. Upload above to make them available to agents.
        </div>
      ) : (
        <ul className="border border-border rounded-sm divide-y divide-border" data-testid="campaign-attach-list">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/40" data-testid={`campaign-attach-row-${a.id}`}>
              <FileText size={14} className="text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{a.label || a.filename}</div>
                {a.label && a.label !== a.filename && (
                  <div className="text-[10px] font-mono text-muted-foreground truncate">{a.filename}</div>
                )}
              </div>
              <span className="text-[10px] font-mono text-muted-foreground shrink-0">{humanBytes(a.size)}</span>
              <Button size="sm" variant="ghost" className="h-7 px-2 rounded-sm" onClick={() => onDownload(a)} data-testid={`campaign-attach-download-${a.id}`}>
                <Download size={12} />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 rounded-sm text-rose-700 hover:text-rose-800" onClick={() => onDelete(a.id)} data-testid={`campaign-attach-delete-${a.id}`}>
                <Trash2 size={12} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const EMPTY_CAMPAIGN = {
  name: "", description: "",
  target_countries: [], target_industries: [],
  target_employee_sizes: [], target_revenue_sizes: [],
  target_departments: [], target_job_functions: "",
  opt_in_required: false, assets: "", special_instructions: "",
  allowed_users: [],
  // Productivity defaults — copied onto each new productivity row when an
  // agent submits a lead under this campaign.
  campaign_id: "",
  campaign_type: "",
  expected_target: 0,
  // Per-campaign-type targets — enables the same campaign to run across
  // multiple campaign_types (Data + LG + DG) with a distinct target
  // per type. Each entry: `{campaign_type, expected_target}`. When any
  // entry is present, its value overrides `expected_target` for
  // productivity rows matching that campaign_type. Legacy single-type
  // campaigns just leave this empty.
  type_targets: [],
  process_spoc: "", process_tl: "", process_owner: "", lead_qualified_by: "",
  attachments: [],
};

function MultiSelect({ label, options, value, onChange, testid, maxHeight = "200px" }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (o) => {
    if (arr.includes(o)) onChange(arr.filter((x) => x !== o));
    else onChange([...arr, o]);
  };
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <label className="meta-label">{label}</label>
        <span className="text-[10px] font-mono text-muted-foreground">{arr.length} selected</span>
      </div>
      <div className="border border-border rounded-sm bg-background p-2 grid grid-cols-2 gap-1 overflow-y-auto" style={{ maxHeight }}>
        {(options || []).map((o) => (
          <label key={o} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-secondary/60 px-1 py-0.5 rounded-sm">
            <Checkbox className="rounded-sm h-3.5 w-3.5" checked={arr.includes(o)} onCheckedChange={() => toggle(o)} />
            <span className="truncate">{o}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function PeopleSelect({ label, people, value, onChange, testid }) {
  // Renders the human-friendly Names of every active platform user.
  // Backwards-compat: if the campaign already has a value not in the list
  // (e.g. a deleted user's name) we still surface it as a pinned option
  // so the admin can see/clear it.
  const opts = value && !people.includes(value) ? [value, ...people] : people;
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</label>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="form-input w-full h-9 rounded-sm mt-0.5 text-sm"
      >
        <option value="">— None —</option>
        {opts.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function TypeTargetsEditor({ value, onChange, campaignTypes = [] }) {
  // Per-campaign-type target rows. Each row: {campaign_type, expected_target}.
  // Admin can add / remove / edit. Kept dead-simple — no drag-reorder, no
  // duplicate-detection (backend just keys by campaign_type, so a dup
  // just means the last one wins on save).
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (idx) => {
    onChange(rows.filter((_, i) => i !== idx));
  };
  const add = () => {
    onChange([...rows, { campaign_type: "", expected_target: 0 }]);
  };

  return (
    <div className="col-span-2 border border-border rounded-sm p-3 bg-muted/20" data-testid="campaign-type-targets-editor">
      <div className="flex items-center justify-between mb-2">
        <label className="meta-label">Per Campaign-Type Targets</label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm h-7 text-xs"
          onClick={add}
          data-testid="campaign-type-target-add-btn"
        >
          + Add row
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic">
          No per-type targets yet. Add rows to set a distinct target per campaign_type — the productivity row for each type auto-fills from the matching row here.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2"
              data-testid={`campaign-type-target-row-${idx}`}
            >
              <select
                data-testid={`campaign-type-target-type-${idx}`}
                value={r.campaign_type || ""}
                onChange={(e) => update(idx, { campaign_type: e.target.value })}
                className="flex-1 rounded-sm h-8 px-2 border border-input bg-background text-xs"
              >
                <option value="">— Select campaign type —</option>
                {campaignTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <Input
                type="number"
                min={0}
                data-testid={`campaign-type-target-value-${idx}`}
                value={r.expected_target ?? 0}
                onChange={(e) => update(idx, { expected_target: e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0 })}
                className="w-24 rounded-sm h-8 text-xs"
                placeholder="Target"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-sm h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                onClick={() => remove(idx)}
                title="Remove row"
                data-testid={`campaign-type-target-remove-${idx}`}
              >
                ×
              </Button>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground italic mt-2 leading-snug">
            Types listed here override the top-level Expected Target for their matching productivity rows. Unlisted campaign_types fall back to the top-level value.
          </p>
        </div>
      )}
    </div>
  );
}


function CampaignEditor({ initial, options, users = [], onSave, onCancel, mode = "edit" }) {
  const [draft, setDraft] = useState(initial);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // Show user-friendly names in the dropdown; the underlying `value` is still the
  // email since that's what the backend matches against `allowed_users`.
  // Sorted alphabetically by display name to make scanning a long list easier.
  const userOptions = [...users]
    .filter((u) => u.email)
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
    .map((u) => ({
      value: u.email,
      label: u.name?.trim() || u.email,
      hint: u.name?.trim() ? u.email : undefined,
    }));
  // Plain list of NAMES (not emails) — used by the Productivity Defaults
  // dropdowns so admins pick a person by name, not by email.
  const peopleNames = [...new Set(
    users
      .map((u) => (u.name || "").trim())
      .filter(Boolean),
  )].sort((a, b) => a.localeCompare(b));
  return (
    <div className="border border-border bg-secondary/30 rounded-sm p-5 space-y-4" data-testid="campaign-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="meta-label">Name</label>
          <Input data-testid="campaign-edit-name" value={draft.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm h-10 mt-1" placeholder="e.g. EMEA Spring Wave" />
        </div>
        <div>
          <label className="meta-label">Job Functions (free text)</label>
          <Input data-testid="campaign-edit-job-functions" value={draft.target_job_functions || ""} onChange={(e) => set("target_job_functions", e.target.value)} className="rounded-sm h-10 mt-1" placeholder="e.g. Marketing Director, Demand Gen Manager" />
        </div>
      </div>
      <div>
        <label className="meta-label">Description</label>
        <Textarea data-testid="campaign-edit-description" value={draft.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-sm mt-1" rows={2} placeholder="Short campaign brief…" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MultiSelect label="Target Countries" testid="campaign-edit-countries" options={options?.countries} value={draft.target_countries} onChange={(v) => set("target_countries", v)} maxHeight="220px" />
        <MultiSelect label="Target Industries" testid="campaign-edit-industries" options={options?.linkedin_industries} value={draft.target_industries} onChange={(v) => set("target_industries", v)} maxHeight="220px" />
        <MultiSelect label="Employee Size Bands" testid="campaign-edit-employee" options={options?.employee_sizes} value={draft.target_employee_sizes} onChange={(v) => set("target_employee_sizes", v)} />
        <MultiSelect label="Revenue Bands" testid="campaign-edit-revenue" options={options?.revenue_sizes} value={draft.target_revenue_sizes} onChange={(v) => set("target_revenue_sizes", v)} />
        <MultiSelect label="Departments" testid="campaign-edit-departments" options={options?.departments} value={draft.target_departments} onChange={(v) => set("target_departments", v)} maxHeight="120px" />
        <div className="space-y-3">
          <label className="meta-label">Opt-in &amp; Assets</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="campaign-edit-optin" checked={!!draft.opt_in_required} onCheckedChange={(c) => set("opt_in_required", !!c)} className="rounded-sm" />
            Opt-in required
          </label>
          <Input data-testid="campaign-edit-assets" value={draft.assets || ""} onChange={(e) => set("assets", e.target.value)} className="rounded-sm h-10" placeholder="Assets list (free text)" />
        </div>
      </div>
      <div>
        <label className="meta-label">Special Instructions</label>
        <Textarea data-testid="campaign-edit-instructions" value={draft.special_instructions || ""} onChange={(e) => set("special_instructions", e.target.value)} className="rounded-sm mt-1" rows={3} placeholder="Things the agent must know while filling the form…" />
      </div>
      {/* Script — attach PDF / DOCX / XLSX / PPTX / TXT documents that
          agents see under Campaign Parameters on the Add Lead form. Total
          cap: 25 MB across ALL attachments on this campaign. */}
      {mode === "edit" && draft.id && (
        <CampaignAttachmentsSection campaignId={draft.id} />
      )}
      {mode === "create" && (
        <div className="border border-dashed border-border rounded-sm p-3 bg-secondary/20 text-[11px] text-muted-foreground">
          <label className="meta-label">Script (attachments)</label>
          <p className="mt-1">Save the campaign first — then you can upload PDF/DOCX/XLSX/PPTX/TXT documents (25 MB total). Agents see them under Campaign Parameters on the Add Lead form.</p>
        </div>
      )}
      {/* Productivity defaults — pre-populate the productivity row created
          on each lead submission. Saves the admin from re-entering the same
          Expected Target / SPOC / TL / Owner / QA assignee across many rows. */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="meta-label">Productivity Defaults</label>
          <span className="text-[10px] text-muted-foreground">Auto-fills new productivity rows</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Campaign ID</label>
            <Input
              data-testid="campaign-edit-campaign-id"
              value={draft.campaign_id || ""}
              onChange={(e) => set("campaign_id", e.target.value)}
              className="rounded-sm h-9 mt-0.5"
              placeholder="e.g. VMSBT001"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Campaign Type</label>
            <select
              data-testid="campaign-edit-campaign-type"
              value={draft.campaign_type || ""}
              onChange={(e) => set("campaign_type", e.target.value)}
              className="w-full rounded-sm h-9 mt-0.5 px-2 border border-input bg-background text-sm"
            >
              <option value="">— Select —</option>
              {(options?.campaign_types || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Expected Target</label>
            <Input
              type="number"
              data-testid="campaign-edit-expected-target"
              value={draft.expected_target ?? 0}
              onChange={(e) => set("expected_target", e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
              className="rounded-sm h-9 mt-0.5"
              placeholder="e.g. 25"
              min={0}
            />
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
              Legacy single-type campaigns use this. For multi-type campaigns, define per-type targets below — those override this for their matching rows.
            </p>
          </div>
          {/* Per-Campaign-Type Targets — added 2026-07-13 for the
              "one campaign, many types" workflow (e.g. same campaign
              running Data + LG + DG with different daily quotas per
              type). When rows exist here, they take precedence over
              the legacy Expected Target for productivity rows that
              match a listed campaign_type. */}
          <TypeTargetsEditor
            value={draft.type_targets || []}
            onChange={(v) => set("type_targets", v)}
            campaignTypes={options?.campaign_types || []}
          />
          <PeopleSelect
            testid="campaign-edit-process-spoc"
            label="Process SPOC"
            people={peopleNames}
            value={draft.process_spoc || ""}
            onChange={(v) => set("process_spoc", v)}
          />
          <PeopleSelect
            testid="campaign-edit-process-tl"
            label="Process TL"
            people={peopleNames}
            value={draft.process_tl || ""}
            onChange={(v) => set("process_tl", v)}
          />
          <PeopleSelect
            testid="campaign-edit-process-owner"
            label="Process Owner"
            people={peopleNames}
            value={draft.process_owner || ""}
            onChange={(v) => set("process_owner", v)}
          />
          <PeopleSelect
            testid="campaign-edit-lead-qualified-by"
            label="Lead Qualified by"
            people={peopleNames}
            value={draft.lead_qualified_by || ""}
            onChange={(v) => set("lead_qualified_by", v)}
          />
        </div>
      </div>
      <div data-testid="campaign-edit-allowed-users-wrap">
        <div className="flex items-center justify-between">
          <label className="meta-label">Allowed Agents</label>
          <span className="text-[10px] font-mono text-muted-foreground">{(draft.allowed_users || []).length} selected</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
          Only users listed here can pick this campaign on the Add Lead form / Campaign Exclusion page — and only when their role is set to &quot;restrict to assigned campaigns&quot; (or their per-user override = Assigned only). Leave empty to grant nobody — or turn restriction off in <span className="font-mono">Admin → Roles → Permissions</span>.
        </p>
        <MultiSelectDropdown
          testid="campaign-edit-allowed-users"
          options={userOptions}
          value={draft.allowed_users || []}
          onChange={(v) => set("allowed_users", v)}
          placeholder={userOptions.length === 0 ? "No users available." : "Select agents…"}
          maxHeight="240px"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={() => onSave(draft)} className="rounded-sm bg-primary hover:bg-[#00227A] h-10" data-testid="campaign-save-button">
          <Check size={14} className="mr-1" /> {mode === "create" ? "Create Campaign" : "Save Campaign"}
        </Button>
        <Button onClick={onCancel} variant="outline" className="rounded-sm h-10" data-testid="campaign-cancel-button">
          <X size={14} className="mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}

function CampaignsTab({ options }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // When a user reaches the Campaigns tab purely via campaign-assignment
  // (Email Marketing Executive / Team Lead / etc. — see /permissions/effective
  // → `surveys_via_assignment`), the tab is rendered SURVEYS-ONLY: campaign
  // CRUD buttons (New / Edit / Delete) are hidden so they don't 403 on click.
  const [surveysOnly, setSurveysOnly] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        if (cancelled) return;
        const p = data?.page_permissions || {};
        // surveys-only when the user does NOT have full admin and reached this
        // tab via the synthetic `surveys_via_assignment` flag.
        setSurveysOnly(p.admin !== true && p.surveys_via_assignment === true);
      } catch { /* assume admin if perms fetch fails — safer for actual admins */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    const [{ data: campaigns }, { data: usersList }] = await Promise.all([
      api.get("/campaigns"),
      // Used to populate the Process SPOC/TL/Owner/Lead-Qualified-By
      // dropdowns + the Allowed-Agents multi-select. We hit the
      // /users/picklist endpoint (no `admin_users` requirement) so a
      // Team Lead with only `admin_campaigns` still sees people names.
      // Fallback to /users for older deployments and double-fallback to
      // an empty array so the editor still renders if both 403.
      api.get("/users/picklist")
        .catch(() => api.get("/users").catch(() => ({ data: [] }))),
    ]);
    setItems(campaigns);
    setUsers(usersList || []);
    // Lead counts are loaded separately so they can poll independently.
    refreshCounts();
  };

  const refreshCounts = async () => {
    setCountsLoading(true);
    try {
      const { data } = await api.get("/campaigns/leads-counts");
      setCounts(data?.counts || {});
    } catch {
      // Non-fatal — UI shows "—" when count is missing.
    } finally { setCountsLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Auto-poll counts every 10s so adds/bulk-uploads from agents/team leads
  // surface here without a manual refresh. Pause when the document is hidden
  // so background tabs don't hammer the API.
  useEffect(() => {
    const tick = () => { if (!document.hidden) refreshCounts(); };
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, []);

  const create = async (draft) => {
    if (!draft.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/campaigns", draft);
      toast.success("Campaign created");
      setCreating(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const update = async (id, draft) => {
    if (!draft.name.trim()) return toast.error("Name is required");
    try {
      await api.put(`/campaigns/${id}`, draft);
      toast.success("Campaign updated");
      setEditingId(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {!creating && !surveysOnly ? (
          <Button onClick={() => setCreating(true)} className="rounded-sm bg-primary hover:bg-[#00227A] h-10" data-testid="campaigns-new-button">
            <Plus size={14} className="mr-1" /> New Campaign
          </Button>
        ) : <div />}
        <button
          onClick={refreshCounts}
          disabled={countsLoading}
          data-testid="campaigns-refresh-counts"
          className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50"
          title="Lead counts refresh automatically every 10s — click to refresh now."
        >
          <RefreshCw size={11} className={countsLoading ? "animate-spin" : ""} /> Refresh counts
        </button>
      </div>
      {surveysOnly && (
        <div className="border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2 rounded-sm" data-testid="campaigns-surveys-only-banner">
          You can manage <strong>surveys</strong> for the campaigns assigned to you below. Ask an admin to change the campaign&apos;s settings.
        </div>
      )}
      {creating && (
        <CampaignEditor
          key="new"
          initial={EMPTY_CAMPAIGN} options={options} users={users} mode="create"
          onSave={create} onCancel={() => setCreating(false)}
        />
      )}

      <div className="space-y-3">
        {(() => {
          // In surveys-only mode, restrict the list to campaigns where the
          // current user appears in `allowed_users`. Server still enforces
          // per-campaign access on every survey API call.
          const myEmail = (user?.email || "").toLowerCase();
          const visible = surveysOnly
            ? items.filter((c) => (c.allowed_users || []).some((u) => String(u).toLowerCase() === myEmail))
            : items;
          if (visible.length === 0) {
            return <div className="text-sm text-muted-foreground border border-border p-6 text-center">
              {surveysOnly ? "You have not been assigned to any campaigns yet. Ask an admin to add you to a campaign's Allowed Agents list." : "No campaigns yet."}
            </div>;
          }
          return visible.map((c) => {
          if (editingId === c.id) {
            return (
              <CampaignEditor
                key={`edit-${c.id}`} initial={c} options={options} users={users}
                onSave={(d) => update(c.id, d)} onCancel={() => setEditingId(null)}
              />
            );
          }
          const leadCount = counts[c.name] || 0;
          return (
            <div key={c.id} className="border border-border p-5 rounded-sm" data-testid={`campaign-row-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="meta-label">{(c.created_at || "").slice(0, 10)}</div>
                  <h3 className="font-display text-lg font-bold inline-flex items-center gap-2">
                    {c.name}
                    <span
                      data-testid={`campaign-leads-count-${c.id}`}
                      className={`text-xs font-mono px-2 py-0.5 rounded-sm ${
                        leadCount > 0
                          ? "bg-primary/10 text-primary border border-primary/30"
                          : "bg-secondary/60 text-muted-foreground border border-border"
                      }`}
                      title={`${leadCount.toLocaleString()} lead${leadCount === 1 ? "" : "s"} in this campaign`}
                    >
                      <Users size={10} className="inline-block -mt-0.5 mr-1" />
                      {leadCount.toLocaleString()} {leadCount === 1 ? "lead" : "leads"}
                    </span>
                  </h3>
                  {c.description && <p className="text-sm text-muted-foreground mt-1">{c.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {!surveysOnly && (
                    <>
                      <button onClick={() => setEditingId(c.id)} data-testid={`campaigns-edit-${c.id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => remove(c.id)} data-testid={`delete-campaigns-${c.id}`} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                        <Trash2 size={12} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
                <Chip label="Leads" value={leadCount.toLocaleString()} highlight={leadCount > 0} />
                <Chip label="Countries" value={c.target_countries?.length} />
                <Chip label="Industries" value={c.target_industries?.length} />
                <Chip label="Employee" value={c.target_employee_sizes?.length} />
                <Chip label="Revenue" value={c.target_revenue_sizes?.length} />
                <Chip label="Departments" value={c.target_departments?.length} />
                <Chip label="Opt-in" value={c.opt_in_required ? "Required" : "—"} />
                <Chip label="Allowed Agents" value={(c.allowed_users || []).length || "—"} />
              </div>
              <CampaignSurveysSection campaign={c} />
            </div>
          );
        });
        })()}
      </div>
    </div>
  );
}

function Chip({ label, value, highlight = false }) {
  return (
    <div
      className={`border px-3 py-1.5 rounded-sm ${
        highlight
          ? "border-primary/40 bg-primary/5"
          : "border-border bg-secondary/30"
      }`}
    >
      <div className="meta-label text-[9px]">{label}</div>
      <div className={`font-mono text-xs mt-0.5 ${highlight ? "text-primary font-semibold" : ""}`}>{value || 0}</div>
    </div>
  );
}

export { CampaignsTab };
